import { useMutation } from '@apollo/client';
import type { UpdateUncontrolledRiskOversightResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateUncontrolledRiskOversightResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateUncontrolledRiskOversightResultInput } from './useUpdateUncontrolledRiskOversightResultTRPC';
import { useUpdateUncontrolledRiskOversightResultTRPC } from './useUpdateUncontrolledRiskOversightResultTRPC';

export const useUpdateUncontrolledRiskOversightResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateGraphQL, graphqlState] = useMutation(
    UpdateUncontrolledRiskOversightResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'oversight_assessment');
        evictField(cache, 'risk_uncontrolled_oversight_result');
        evictField(cache, 'risk_uncontrolled_oversight_result_aggregate');
      },
    }
  );

  const trpcMutation = useUpdateUncontrolledRiskOversightResultTRPC();

  const updateUncontrolledRiskOversightResult = async (
    variables: UpdateUncontrolledRiskOversightResultInput
  ): Promise<UpdateUncontrolledRiskOversightResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateUncontrolledRiskOversightResult(variables);
    }

    const result = await updateGraphQL({
      variables: {
        Id: variables.Id,
        Impact: variables.Impact,
        Likelihood: variables.Likelihood,
        Rating: variables.Rating,
        Rationale: variables.Rationale,
        TestDate: variables.TestDate,
        CustomAttributeData: variables.CustomAttributeData ?? null,
      },
    });
    if (!result.data) {
      throw new Error('Failed to update uncontrolled risk oversight result');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateUncontrolledRiskOversightResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateUncontrolledRiskOversightResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
